import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from "reactstrap";
import { useFormik } from "formik";
import { object, string, array } from "yup";
import ToastHandler from "../../../components/Common/ToastHandler";
import useUserAuthStore from "../../../zStore/userAuth";

const rejectionReasons = [
  "Inappropriate content",
  "Violates community guidelines",
  "Spam or misleading information",
  "Incorrect location",
  "Duplicate submission",
  "Low quality images",
  "Inappropriate images",
  "Check the image dimensions",
  "Other"
];

const schema = object({
  reasons: array().min(1, "Please select at least one reason").required("Reasons are required"),
  comment: string().required("Comment is required").min(10, "Comment must be at least 10 characters"),
  otherReason: string().when("reasons", {
    is: (reasons) => reasons && reasons.includes("Other"),
    then: (schema) => schema.required("Please specify the other reason").min(3, "Other reason must be at least 3 characters"),
    otherwise: (schema) => schema,
  }),
});

const RejectionModal = ({ isOpen, toggle, hotspot, onConfirm }) => {
  const [toast, setToast] = useState(false);
  const [toastData, setToastData] = useState({ title: "", message: "" });
  const { data: userData } = useUserAuthStore();

  const formik = useFormik({
    initialValues: {
      reasons: [],
      comment: "",
      otherReason: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        // Get current admin/user ID - try multiple possible field names
        const adminId = userData?.user_id || 
                       userData?.userId || 
                       userData?.id || 
                       userData?.userID ||
                       (userData && typeof userData === 'object' ? Object.values(userData).find(v => typeof v === 'string' && v.length > 10) : null) ||
                       "unknown";
        
        // Validate "Other" reason has text
        if (values.reasons.includes("Other") && !values.otherReason?.trim()) {
          setToastData({
            title: "Validation Error",
            message: "Please specify the other reason",
          });
          setToast(true);
          return;
        }
        
        // Process reasons - replace "Other" with otherReason text
        const processedReasons = values.reasons
          .map(reason => reason === "Other" ? values.otherReason.trim() : reason)
          .filter(reason => reason && reason.trim()); // Remove any empty strings

        // Create rejection reason object
        const rejectionReason = {
          by_admin: adminId,
          timestamp: new Date().toISOString(),
          reasons: processedReasons,
          comment: values.comment,
        };

        // Call the confirm callback with the rejection data
        await onConfirm(hotspot, rejectionReason);
        toggle();
      } catch (error) {
        setToastData({
          title: "Error",
          message: error.message || "Failed to submit rejection",
        });
        setToast(true);
      }
    },
  });

  const handleReasonChange = (reason) => {
    const currentReasons = formik.values.reasons;
    if (currentReasons.includes(reason)) {
      formik.setFieldValue(
        "reasons",
        currentReasons.filter((r) => r !== reason)
      );
    } else {
      formik.setFieldValue("reasons", [...currentReasons, reason]);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
        <ModalHeader className="bg-danger text-white" toggle={toggle}>
          <i className="mdi mdi-alert-circle me-2"></i>
          Reject Hotspot Request
        </ModalHeader>
        <form onSubmit={formik.handleSubmit}>
          <ModalBody className="p-4">
            <div className="mb-4">
              <p className="text-muted mb-0">
                You are about to reject: <strong>{hotspot?.tag_name}</strong>
              </p>
              <p className="text-muted small">Request ID: #{hotspot?.request_id}</p>
            </div>

            <FormGroup className="mb-4">
              <Label className="fw-bold mb-3">
                Select Reason(s) <span className="text-danger">*</span>
              </Label>
              <div className="d-flex flex-column gap-2">
                {rejectionReasons.map((reason) => (
                  <div key={reason} className="form-check">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id={`reason-${reason}`}
                      checked={formik.values.reasons.includes(reason)}
                      onChange={() => handleReasonChange(reason)}
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`reason-${reason}`}
                      style={{ cursor: "pointer" }}
                    >
                      {reason}
                    </Label>
                  </div>
                ))}
              </div>
              {formik.touched.reasons && formik.errors.reasons && (
                <div className="text-danger small mt-2">{formik.errors.reasons}</div>
              )}
            </FormGroup>

            {formik.values.reasons.includes("Other") && (
              <FormGroup className="mb-4">
                <Label htmlFor="otherReason" className="fw-bold">
                  Specify Other Reason <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="otherReason"
                  name="otherReason"
                  placeholder="Please specify the reason..."
                  value={formik.values.otherReason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={
                    formik.touched.otherReason && formik.errors.otherReason
                      ? "is-invalid"
                      : ""
                  }
                />
                {formik.touched.otherReason && formik.errors.otherReason && (
                  <div className="text-danger small mt-1">{formik.errors.otherReason}</div>
                )}
              </FormGroup>
            )}

            <FormGroup className="mb-4">
              <Label htmlFor="comment" className="fw-bold">
                Additional Comment <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                id="comment"
                name="comment"
                rows="4"
                placeholder="Please provide additional information about the rejection..."
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={
                  formik.touched.comment && formik.errors.comment
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.touched.comment && formik.errors.comment && (
                <div className="text-danger small mt-1">{formik.errors.comment}</div>
              )}
              <div className="text-muted small mt-1">
                Minimum 10 characters required
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={toggle} type="button">
              Cancel
            </Button>
            <Button
              color="danger"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
      <ToastHandler
        open={toast}
        setOpen={setToast}
        duration={3000}
        message={toastData.message}
        title={toastData.title}
      />
    </>
  );
};

export default RejectionModal;

